import type { PipeTransform } from '@angular/core';
import { Pipe } from '@angular/core';

import moment from 'moment-timezone';

import type { DateRangeValue } from '@recruitee/date-range-types';
import { DateRangeValueType } from '@recruitee/date-range-types';
import { I18nService } from '@recruitee/i18n';

// eslint-disable-next-line max-lines-per-function
export const getDateRangePickerLabel = (value: DateRangeValue, i18n: I18nService): string => {
  switch (value.type) {
    case DateRangeValueType.Today:
      return i18n.translate('app.common.today');
    case DateRangeValueType.Yesterday:
      return i18n.translate('app.common.yesterday');
    case DateRangeValueType.Tomorrow:
      return i18n.translate('app.common.tomorrow');
    case DateRangeValueType.ThisWeek:
      return i18n.translate('app.common.this_week');
    case DateRangeValueType.LastWeek:
      return i18n.translate('app.common.last_week');
    case DateRangeValueType.NextWeek:
      return i18n.translate('app.common.next_week');
    case DateRangeValueType.ThisMonth:
      return i18n.translate('app.common.this_month');
    case DateRangeValueType.NextMonth:
      return i18n.translate('app.common.next_month');
    case DateRangeValueType.LastMonth:
      return i18n.translate('app.common.last_month');
    case DateRangeValueType.ThisQuarter:
      return i18n.translate('app.common.this_quarter');
    case DateRangeValueType.NextQuarter:
      return i18n.translate('app.common.next_quarter');
    case DateRangeValueType.LastQuarter:
      return i18n.translate('app.common.last_quarter');
    case DateRangeValueType.ThisYear:
      return i18n.translate('app.common.this_year');
    case DateRangeValueType.NextYear:
      return i18n.translate('app.common.next_year');
    case DateRangeValueType.LastYear:
      return i18n.translate('app.common.last_year');
    case DateRangeValueType.Last7Days:
      return i18n.translate('app.common.last_x_days', { count: 7 });
    case DateRangeValueType.Last14Days:
      return i18n.translate('app.common.last_x_days', { count: 14 });
    case DateRangeValueType.Last20Days:
      return i18n.translate('app.common.last_x_days', { count: 20 });
    case DateRangeValueType.Last30Days:
      return i18n.translate('app.common.last_x_days', { count: 30 });
    case DateRangeValueType.Last60Days:
      return i18n.translate('app.common.last_x_days', { count: 60 });
    case DateRangeValueType.Last90Days:
      return i18n.translate('app.common.last_x_days', { count: 90 });
    case DateRangeValueType.Last365Days:
      return i18n.translate('app.common.last_x_days', { count: 365 });
    case DateRangeValueType.AllTime:
      return i18n.translate('app.common.all_time');
    case DateRangeValueType.Range:
      return i18n.translate('app.common.date_range');
    case DateRangeValueType.Exact:
      return i18n.translate('app.common.exact_date');
    case DateRangeValueType.WithinNext:
      return i18n.translate('app.common.within_next');
    case DateRangeValueType.WithinLast:
      return i18n.translate('app.common.within_last');

    default:
    case DateRangeValueType.Custom:
      if (value.min && value.max) {
        return [value.min!, value.max!].map(v => moment(v).format('DD MMM YYYY')).join(' - ');
      }

      return i18n.translate(`app.common.custom_date`);
  }
};

@Pipe({
  name: 'rtDateRangePickerLabel',
  pure: true,
  standalone: true,
})
export class DateRangePickerLabelPipe implements PipeTransform {
  constructor(private i18n: I18nService) {}

  public transform(type: DateRangeValueType, opts: { min?: string; max?: string } = {}): any {
    return getDateRangePickerLabel({ type, ...opts }, this.i18n);
  }
}
